import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { MouseEventHandler, useCallback, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { getPositionDisplayPrice } from '@flipster/r230522/original/ticker-price';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import TrendIcon from '@flipster/react/icon/generated/TrendIcon';
import { css } from '@flipster/styled-system/css';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { ProfitBoostEvent } from '@flipster/utils/segment/events/profit-boost';

import { SymbolScope } from '../../symbol/bunja';
import { tradingSessionCheckBunja } from '../../trading-suspension/state';
import { ProfitBoostModal } from '../profit-boost-modal';
import { ProfitCountDown } from './profit-countdown';
import { ProfitBoostPositionIdScope, profitBoostBadgeBunja } from './state';

interface Props {
  positionId: string;
  symbol: string;
  location: 'assets_tab' | 'order_form_page'; // only for logging
}

export const ProfitBoostBadge = ({ positionId, symbol, location }: Props) => {
  const overlay = useOverlay();
  const { checkTradable } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(symbol)]);
  const { positionAtom, boostExpiryEpochAtom, isOneWayModeAtom } = useBunja(profitBoostBadgeBunja, [
    ProfitBoostPositionIdScope.bind(positionId),
  ]);
  const boostExpiryEpoch = useAtomValue(boostExpiryEpochAtom);
  const [isExpired, setIsExpired] = useState(false);

  const targetDate = boostExpiryEpoch != null ? new Date(boostExpiryEpoch) : undefined;

  // 통짜 position/one-way는 클릭 시점에만 lazy read → 매 tick 구독/리렌더 없음.
  const openModal = useAtomCallback(
    useCallback(
      (get) => {
        if (!checkTradable()) return;
        const position = get(positionAtom);
        if (!position) return;

        segment.track(ProfitBoostEvent.ProfitBoostClick, {
          event_location: location,
          position_id: position.positionId,
          symbol: position.symbol,
          quantity: Num(position.quantity).toNumber(),
          leverage: Num(position.initLeverage).toNumber(),
          current_price: Num(getPositionDisplayPrice(position)).toNumber(),
          side: Num(position.position).isGreaterThanOrEqualTo(0) ? 'buy' : 'sell',
          margin_usdt: Num(position.fundsInvested).toNumber(),
          unrealized_pnl: Num(position.unrealizedPnl).toNumber(),
          unrealized_pnl_percentage: Num(position.calcUnrealizedPnlPercent).toNumber(),
          position_mode: get(isOneWayModeAtom) ? 'oneway' : 'multiposition',
        });

        overlay.open(({ isOpen, onClose }) => (
          <ProfitBoostModal
            isOpen={isOpen}
            positionId={position.positionId}
            onClose={onClose}
            symbol={position.symbol}
            location={location}
          />
        ));
      },
      [checkTradable, positionAtom, isOneWayModeAtom, overlay, location],
    ),
  );

  const handleClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    openModal();
  };

  const showCountdown = !isExpired && targetDate;

  if (showCountdown) {
    return <ProfitCountDown targetDate={targetDate} onEnd={() => setIsExpired(true)} />;
  }

  return (
    <button
      type='button'
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        color: 'inverse.white',
        bgGradient: 'programs.promotion',
        textStyle: 'Text/XS/Regular',
        p: '2px 4px 2px 8px',
        borderRadius: 50,
      })}
      onClick={handleClick}
    >
      <TrendIcon size={12} />
      <span className={css({ ml: 2, mr: 4, whiteSpace: 'nowrap' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Boost' />
      </span>
      <ChevronRightIcon size={12} />
    </button>
  );
};
